import React from 'react';
import { MapPin, Phone, Navigation, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const LocationsSection = () => {
  const { t, direction } = useLanguage();

  // Tunisia regions with mock POS distribution
  const tunisiaRegions = [
    { name: 'Tunis', posCount: 45, color: 'bg-red-500' },
    { name: 'Sfax', posCount: 28, color: 'bg-blue-500' },
    { name: 'Sousse', posCount: 32, color: 'bg-green-500' },
    { name: 'Kairouan', posCount: 18, color: 'bg-yellow-500' },
    { name: 'Bizerte', posCount: 22, color: 'bg-purple-500' },
    { name: 'Gabès', posCount: 15, color: 'bg-pink-500' },
    { name: 'Ariana', posCount: 20, color: 'bg-indigo-500' },
    { name: 'Autres', posCount: 20, color: 'bg-orange-500' }
  ];

  return (
    <section id="localisation" className="py-20 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 ${direction === 'rtl' ? 'text-right' : ''}`}>
          <span className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-full text-sm font-semibold">
            {t('locations.title')}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-6 mb-4">
            {t('locations.subtitle')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('locations.findNearest')}
          </p>
        </div>

        {/* Map Placeholder & Regions */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Tunisia Map Visualization */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <div className="relative aspect-square bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl overflow-hidden">
              {/* Tunisia Map SVG Placeholder */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-full h-full p-8">
                  {/* Animated points representing POS */}
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-3 h-3 bg-orange-500 rounded-full animate-ping"
                      style={{
                        top: `${Math.random() * 80 + 10}%`,
                        left: `${Math.random() * 80 + 10}%`,
                        animationDelay: `${i * 0.2}s`
                      }}
                    >
                      <MapPin className="w-full h-full text-orange-600" />
                    </div>
                  ))}
                  
                  {/* Tunisia shape representation */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="w-24 h-24 text-blue-600 mx-auto mb-4" />
                      <div className="text-6xl font-bold text-blue-900">200+</div>
                      <div className="text-xl text-gray-700 mt-2">{t('locations.pointsOfSale')}</div>
                      <div className="text-sm text-gray-500 mt-1">{t('locations.acrossTunisia')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact for nearest POS */}
            <div className="mt-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-xl">
              <div className={`flex items-center gap-3 mb-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Phone className="w-6 h-6" />
                <h3 className="text-lg font-bold">{t('locations.findNearest')}</h3>
              </div>
              <p className="text-orange-100 mb-4">
                {t('locations.findNearestDesc')}
              </p>
              <a href="tel:+21699804002" className={`inline-flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-all ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Phone className="w-5 h-5" />
                +216 99 804 002
              </a>
              <a
                href="https://wa.me/21695806095?text=Bonjour%20Olion%2C%20je%20viens%20de%20visiter%20votre%20site%20et%20j'aimerais%20avoir%20plus%20d'informations."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="locations-whatsapp-link"
                className={`mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all ${direction === 'rtl' ? 'flex-row-reverse mr-3' : 'ml-3'}`}
              >
                <MessageCircle className="w-5 h-5" fill="white" />
                WhatsApp · +216 95 806 095
              </a>
            </div>
          </div>

          {/* Regions List */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {t('locations.distributionByRegion')}
            </h3>
            
            {tunisiaRegions.map((region, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 group"
              >
                <div className={`flex items-center justify-between ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-12 h-12 ${region.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{region.name}</h4>
                      <p className="text-sm text-gray-500">{t('locations.governorate')}</p>
                    </div>
                  </div>
                  
                  <div className={direction === 'rtl' ? 'text-left' : 'text-right'}>
                    <div className="text-3xl font-bold text-blue-600">{region.posCount}</div>
                    <div className="text-sm text-gray-500">{t('locations.regionPos')}</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${region.color} transition-all duration-1000`}
                    style={{ width: `${(region.posCount / 45) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}

            {/* Total */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-8 shadow-xl mt-8">
              <div className={`flex items-center justify-between ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <div>
                  <div className="text-sm text-blue-200 mb-1">{t('locations.totalTunisia')}</div>
                  <div className="text-5xl font-bold">200+</div>
                  <div className="text-blue-100 mt-2">{t('locations.activePos')}</div>
                </div>
                <Navigation className="w-20 h-20 text-white/30" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
